import React, { useState } from 'react';
import infoIcon from '../../../assets/icons/info-icon-black.svg';
import urlTransferIcon from '../../../assets/icons/url_transfer_table.svg';

// File name with truncation and tooltip
export const FileNameCell = ({ value, maxLength = 20 }) => {
  if (!value) return "---";
  
  if (value.length <= maxLength) {
    return value;
  }
  
  // Get file extension
  const lastDotIndex = value.lastIndexOf('.');
  const name = lastDotIndex !== -1 ? value.substring(0, lastDotIndex) : value;
  const extension = lastDotIndex !== -1 ? value.substring(lastDotIndex) : '';
  
  // Truncate name part if needed
  const truncatedName = name.length > maxLength - extension.length 
    ? name.substring(0, maxLength - extension.length - 3) + '...' 
    : name;
  
  return (
    <span 
      title={value} 
      style={{ 
        cursor: 'help',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap'
      }}
    >
      {truncatedName + extension}
    </span>
  );
};

// User name with truncation and tooltip (uses "Арнолд Шварценегер" as reference for max width)
export const UserNameCell = ({ value, row, maxLength = 18 }) => {
  // Handle different data structures for user names
  let userName = null;
  
  // If value is a string, use it directly
  if (typeof value === 'string') {
    userName = value;
  }
  // If value is an object, extract fullName
  else if (value && typeof value === 'object' && value.fullName) {
    userName = value.fullName;
  }
  // If value is not provided or is empty, check row for nested user data
  else if (row) {
    // Common nested patterns for user data
    if (row.createdBy?.fullName) userName = row.createdBy.fullName;
    else if (row.user?.fullName) userName = row.user.fullName;
    else if (row.author?.fullName) userName = row.author.fullName;
    else if (row.invitedBy?.fullName) userName = row.invitedBy.fullName;
    else if (row.acceptedBy?.fullName) userName = row.acceptedBy.fullName;
    else if (row.lastMessageSentBy?.fullName) userName = row.lastMessageSentBy.fullName;
  }
  
  // Ensure userName is a string and not empty
  if (!userName || typeof userName !== 'string') return "---";
  
  if (userName.length <= maxLength) {
    return userName;
  }
  
  // Truncate name with ellipsis
  const truncatedName = userName.substring(0, maxLength - 3) + '...';
  
  return (
    <span 
      title={userName} 
      style={{ 
        cursor: 'help',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        maxWidth: '100%',
        display: 'inline-block'
      }}
    >
      {truncatedName}
    </span>
  );
};

// Title with truncation and tooltip - reusable for any text content
export const TitleCell = ({ value, maxLength = 25 }) => {
  if (!value || typeof value !== 'string') return "---";
  
  if (value.length <= maxLength) {
    return value;
  }
  
  // Truncate title with ellipsis
  const truncatedTitle = value.substring(0, maxLength - 3) + '...';
  
  return (
    <span 
      title={value} 
      style={{ 
        cursor: 'help',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        maxWidth: '100%',
        display: 'inline-block'
      }}
    >
      {truncatedTitle}
    </span>
  );
};

// Status badge with color coding
export const StatusBadge = ({ status, type = 'default' }) => {
  const getStatusConfig = (status, type) => {
    switch (type) {
      case 'payment':
        switch (status) {
          case 'paid':
            return { className: 'status-paid', text: 'Paid' };
          case 'unpaid':
            return { className: 'status-unpaid', text: 'Unpaid' };
          case 'failed':
            return { className: 'status-failed', text: 'Failed' };
          default:
            return { className: 'status-unknown', text: status };
        }
      case 'chat':
        switch (status) {
          case 'active':
            return { className: 'status-active', text: 'Active' };
          case 'archived':
            return { className: 'status-archived', text: 'Archived' };
          default:
            return { className: 'status-unknown', text: status };
        }
      default:
        return { className: 'status-default', text: status };
    }
  };

  const config = getStatusConfig(status, type);

  return (
    <span className={`status-badge ${config.className}`}>
      {config.text}
    </span>
  );
};

// Payment type badge
export const PaymentTypeBadge = ({ type }) => {
  const getPaymentTypeDisplay = (type) => {
    switch (type) {
      case 'pay-rent':
        return { className: 'pay-rent', text: 'Full Payment' };
      case 'partial-pay-rent':
        return { className: 'partial-pay-rent', text: 'Partial Payment' };
      default:
        return { className: 'default', text: type || '---' };
    }
  };

  const config = getPaymentTypeDisplay(type);

  return (
    <span className={`payment-type ${config.className}`}>
      {config.text}
    </span>
  );
};

// Reminders cell component with tooltip
export const RemindersCell = ({ reminders }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const enabledCount = reminders?.filter(reminder => reminder.isEnabled)?.length || 0;

  const RemindersTooltip = ({ reminders, isVisible }) => {
    if (!isVisible || !reminders || !Array.isArray(reminders)) return null;

    const enabledReminders = reminders.filter(reminder => reminder.isEnabled);

    return (
      <div className="reminders-tooltip">
        <div className="tooltip-arrow"></div>
        <div className="tooltip-content">
          {enabledReminders.map((reminder, index) => (
            <div key={reminder._id || index} className="reminder-item">
              <div className="reminder-label">
                {index === 0 ? "First reminder" : 
                 index === 1 ? "Second reminder" : 
                 index === 2 ? "Third reminder" : 
                 `${index + 1}th reminder`}
              </div>
              <div className="reminder-time">
                <strong>{reminder.amount} {reminder.type}</strong>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="reminders-cell">
      <span className="reminders-count">{enabledCount}</span>
      {enabledCount > 0 && (
        <div 
          className="info-icon-container"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <img src={infoIcon} alt="Info" className="info-icon" />
          <RemindersTooltip reminders={reminders} isVisible={showTooltip} />
        </div>
      )}
    </div>
  );
};

// Yes/No indicator
export const YesNoCell = ({ value, positiveKey = 'filesCount' }) => {
  const hasValue = positiveKey ? value?.[positiveKey] > 0 : value;
  return (
    <span className={`indicator ${hasValue ? 'yes' : 'no'}`}>
      {hasValue ? 'Yes' : 'No'}
    </span>
  );
};

// Connected Event Cell for Calendar Communities
export const ConnectedEventCell = ({ value, row }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const isConnected = Array.isArray(value) && value.length > 0;

  const ConnectedEventTooltip = ({ isVisible }) => {
    if (!isVisible || !isConnected) return null;

    return (
      <div className="info-tooltip">
        <div className="tooltip-content connected-event-tooltip">
          <div className="tooltip-line connected-event-line">Connected to</div>
          <div className="tooltip-line-bold connected-event-bold"><strong>Voting poll</strong></div>
        </div>
        <div className="tooltip-arrow"></div>
      </div>
    );
  };

  return (
    <div className="connected-event-container">
      <span className={`connected-event ${isConnected ? 'connected' : 'standalone'}`}>
        {isConnected ? "Yes" : "No"}
      </span>
      {isConnected && (
        <div 
          className="info-icon-wrapper"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <img src={infoIcon} alt="Info" className="info-icon" />
          <ConnectedEventTooltip isVisible={showTooltip} />
        </div>
      )}
    </div>
  );
};

// Enhanced Reminders Cell for Calendar Communities
export const CalendarRemindersCell = ({ value, reminders }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  // Use value if passed from table preset, otherwise use reminders prop
  const reminderData = value || reminders;
  const enabledCount = reminderData?.filter(reminder => reminder.isEnabled)?.length || 0;
  const enabledReminders = reminderData?.filter(reminder => reminder.isEnabled) || [];

  const getOrdinalText = (index) => {
    switch (index) {
      case 0: return 'First reminder';
      case 1: return 'Second reminder';
      case 2: return 'Third reminder';
      default: return `${index + 1}th reminder`;
    }
  };

  const formatTimeUnit = (amount, type) => {
    // Handle singular/plural forms
    if (amount === 1) {
      // Remove 's' from plural forms for singular
      if (type.endsWith('s')) {
        return `${amount} ${type.slice(0, -1)}`;
      }
      return `${amount} ${type}`;
    } else {
      // Ensure plural form
      if (!type.endsWith('s')) {
        return `${amount} ${type}s`;
      }
      return `${amount} ${type}`;
    }
  };

  const CalendarRemindersTooltip = ({ isVisible }) => {
    if (!isVisible || enabledCount === 0) return null;

    return (
      <div className="info-tooltip">
        <div className="tooltip-content calendar-reminders-tooltip">
          {enabledReminders.map((reminder, index) => (
            <div key={index} className="reminder-group">
              <div className="reminder-label">{getOrdinalText(index)}</div>
              <div className="reminder-value"><strong>{formatTimeUnit(reminder.amount, reminder.type)}</strong></div>
              {index < enabledReminders.length - 1 && <div className="reminder-gap"></div>}
            </div>
          ))}
        </div>
        <div className="tooltip-arrow"></div>
      </div>
    );
  };

  return (
    <div className="reminders-container">
      <span className="reminders-count">{enabledCount}</span>
      {enabledCount > 0 && (
        <div 
          className="info-icon-wrapper"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <img src={infoIcon} alt="Info" className="info-icon" />
          <CalendarRemindersTooltip isVisible={showTooltip} />
        </div>
      )}
    </div>
  );
};

// --- Custom cell components for MeetingsCommunities ---

export const MeetingTitleCell = ({ value, row }) => (
  <div className="title-with-icon-container">
    <span className="title-text">{value || '---'}</span>
    <div className="info-icon-wrapper">
      <img 
        src={urlTransferIcon}
        alt="Link" 
        className="info-icon-clickable"
        onClick={() => {
          if (row.calendarEvent) {
            // Open calendar event link or meeting details
            window.open(`/meeting/${row._id}`, '_blank');
          }
        }}
      />
    </div>
  </div>
);

export const MeetingScheduledTimeCell = ({ value, row }) => {
  if (!value || !row.endDate) return '---';
  
  const startTime = new Date(value).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  const endTime = new Date(row.endDate).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  
  return `${startTime}-${endTime}`;
};

export const MeetingStatusCell = ({ value }) => {
  // Simple text display without borders or background
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : '---';
};

export const MeetingRealTimeCell = ({ value, row }) => {
  // Use startedAt and endedAt for real time range
  const startedAt = value; // value is startedAt
  const endedAt = row.endedAt;
  
  if (!startedAt || !endedAt) return '---';
  
  const startTime = new Date(startedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  const endTime = new Date(endedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  
  return `${startTime}-${endTime}`;
};

export const MeetingDurationCell = ({ value, row }) => {
  // Calculate duration from startedAt and endedAt if available
  const startedAt = row.startedAt;
  const endedAt = row.endedAt;
  
  if (startedAt && endedAt) {
    const durationMs = new Date(endedAt) - new Date(startedAt);
    const durationMinutes = Math.floor(durationMs / (1000 * 60));
    
    if (durationMinutes < 60) {
      return `${durationMinutes} Min`;
    } else {
      const hours = Math.floor(durationMinutes / 60);
      const minutes = durationMinutes % 60;
      if (minutes === 0) {
        return `${hours} h`;
      } else {
        return `${hours} h ${minutes} Min`;
      }
    }
  }
  
  // Fallback to provided duration value if available
  if (value) {
    // If value is in minutes
    if (typeof value === 'number') {
      if (value < 60) {
        return `${value} Min`;
      } else {
        const hours = Math.floor(value / 60);
        const minutes = value % 60;
        if (minutes === 0) {
          return `${hours} h`;
        } else {
          return `${hours} h ${minutes} Min`;
        }
      }
    }
    return value;
  }
  
  return '---';
};

export const MeetingParticipantsCell = ({ value }) => {
  return value || 0;
};

export const MeetingRecordingCell = ({ value, row }) => {
  const hasRecording = value === 'completed' && row.recordingUrl;
  return (
    <div className="recording-with-icon-container">
      <span>{hasRecording ? 'Yes' : 'No'}</span>
      {hasRecording && (
        <div className="info-icon-wrapper">
          <img 
            src={urlTransferIcon}
            alt="Recording Link" 
            className="info-icon-clickable"
            onClick={() => {
              if (row.recordingUrl) {
                window.open(row.recordingUrl, '_blank');
              }
            }}
          />
        </div>
      )}
    </div>
  );
};

export const MeetingTranscriptCell = ({ value, row }) => {
  const hasTranscript = value === 'completed' && row.transcriptionUrl;
  return (
    <div className="transcript-with-icon-container">
      <span>{hasTranscript ? 'Yes' : 'No'}</span>
      {hasTranscript && (
        <div className="info-icon-wrapper">
          <img 
            src={urlTransferIcon}
            alt="Transcript Link" 
            className="info-icon-clickable"
            onClick={() => {
              if (row.transcriptionUrl) {
                window.open(row.transcriptionUrl, '_blank');
              }
            }}
          />
        </div>
      )}
    </div>
  );
};

export const MeetingRemindersCell = ({ value, row }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  // Get reminders from calendarEvent if available
  const calendarEvent = row.calendarEvent;
  const reminders = calendarEvent?.reminders || [];
  const enabledReminders = reminders.filter(reminder => reminder.isEnabled) || [];
  const enabledCount = enabledReminders.length;

  const getOrdinalText = (index) => {
    switch (index) {
      case 0: return 'First reminder';
      case 1: return 'Second reminder';
      case 2: return 'Third reminder';
      default: return `${index + 1}th reminder`;
    }
  };

  const formatTimeUnit = (amount, type) => {
    // Handle singular/plural forms
    if (amount === 1) {
      // Remove 's' from plural forms for singular
      if (type.endsWith('s')) {
        return `${amount} ${type.slice(0, -1)}`;
      }
      return `${amount} ${type}`;
    } else {
      // Ensure plural form
      if (!type.endsWith('s')) {
        return `${amount} ${type}s`;
      }
      return `${amount} ${type}`;
    }
  };

  const MeetingRemindersTooltip = ({ isVisible }) => {
    if (!isVisible || enabledCount === 0) return null;

    return (
      <div className="info-tooltip">
        <div className="tooltip-content calendar-reminders-tooltip">
          {enabledReminders.map((reminder, index) => (
            <div key={index} className="reminder-group">
              <div className="reminder-label">{getOrdinalText(index)}</div>
              <div className="reminder-value"><strong>{formatTimeUnit(reminder.amount, reminder.type)}</strong></div>
              {index < enabledReminders.length - 1 && <div className="reminder-gap"></div>}
            </div>
          ))}
        </div>
        <div className="tooltip-arrow"></div>
      </div>
    );
  };

  return (
    <div className="reminders-container">
      <span className="reminders-count">{enabledCount > 0 ? 'Yes' : 'No'}</span>
      {enabledCount > 0 && (
        <div 
          className="info-icon-wrapper"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <img src={infoIcon} alt="Info" className="info-icon" />
          <MeetingRemindersTooltip isVisible={showTooltip} />
        </div>
      )}
    </div>
  );
}; 